import React, { useEffect } from 'react';
import { cn } from '@/utils/cn';
import type { MetaGender, MetaSpecialAdCategory } from '@/services/api/metaAds';
import { AGE_RESTRICTED_CATEGORIES } from '../../constants/metaAdSetMatrix';

interface DemographicsSectionProps {
  ageMin: number;
  ageMax: number;
  genders: MetaGender[];
  /** Parent campaign's special_ad_categories. When HOUSING / EMPLOYMENT /
   *  CREDIT is present, Meta enforces both `age_min >= 18` AND `genders`
   *  set to `[MALE, FEMALE]` (no gender-narrowed targeting allowed for
   *  regulated categories). This component locks both fields accordingly. */
  parentSpecialAdCategories: MetaSpecialAdCategory[];
  onChangeAgeMin: (age: number) => void;
  onChangeAgeMax: (age: number) => void;
  onChangeGenders: (genders: MetaGender[]) => void;
  error?: string;
}

/**
 * Age + gender targeting. Meta requires age 13-65 for general campaigns,
 * 18-65 for HOUSING / EMPLOYMENT / CREDIT special categories. The wizard
 * locks age_min to 18 when any of those categories is present on the parent
 * campaign — backend enforces too (defense-in-depth).
 */
export const DemographicsSection: React.FC<DemographicsSectionProps> = ({
  ageMin,
  ageMax,
  genders,
  parentSpecialAdCategories,
  onChangeAgeMin,
  onChangeAgeMax,
  onChangeGenders,
  error,
}) => {
  // HOUSING / EMPLOYMENT / CREDIT flip both age AND gender into locked
  // mode: age_min must be >= 18 and genders must include both. Meta
  // rejects gender-narrowed regulated targeting.
  const regulated = parentSpecialAdCategories.some((c) =>
    AGE_RESTRICTED_CATEGORIES.has(c),
  );
  const ageLocked = regulated;
  const gendersLocked = regulated;
  const effectiveMin = ageLocked ? Math.max(18, ageMin) : ageMin;

  // When genders are locked we force both to be selected. Effect-only
  // sync so callers see a corrected payload even before they touch
  // the gender buttons. `onChangeGenders` is intentionally in the dep
  // list — callers often pass inline lambdas that recreate per render,
  // and the inner guard (`gendersLocked && genders.length !== 2`)
  // keeps the effect body a no-op when there's nothing to correct,
  // so re-firing on identity change is harmless.
  useEffect(() => {
    if (gendersLocked && (genders.length !== 2)) {
      onChangeGenders(['MALE', 'FEMALE']);
    }
  }, [gendersLocked, genders.length, onChangeGenders]);

  const toggleGender = (g: MetaGender) => {
    if (gendersLocked) return; // no-op while locked
    if (genders.includes(g)) {
      // Don't allow empty — leave at least one gender selected.
      if (genders.length > 1) {
        onChangeGenders(genders.filter((x) => x !== g));
      }
    } else {
      onChangeGenders([...genders, g]);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-2">
          {regulated
            ? 'Age is locked to 18+ and gender is locked to all because the parent campaign has a regulated special ad category (housing, employment, or credit). Meta rejects narrower targeting in those categories.'
            : 'Set the age and gender of the audience.'}
        </p>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            Age range
          </label>
          <span className="text-sm text-gray-900 font-medium">
            {effectiveMin}–{ageMax}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="meta-targeting-age-min"
              className="block text-xs text-gray-500 mb-1"
            >
              Minimum {ageLocked && '(18+ enforced)'}
            </label>
            <input
              id="meta-targeting-age-min"
              type="range"
              min={ageLocked ? 18 : 13}
              max={65}
              value={effectiveMin}
              onChange={(e) => onChangeAgeMin(parseInt(e.target.value, 10))}
              className="w-full"
              aria-valuemin={ageLocked ? 18 : 13}
              aria-valuemax={65}
              aria-valuenow={effectiveMin}
            />
          </div>
          <div>
            <label
              htmlFor="meta-targeting-age-max"
              className="block text-xs text-gray-500 mb-1"
            >
              Maximum
            </label>
            <input
              id="meta-targeting-age-max"
              type="range"
              min={ageLocked ? 18 : 13}
              max={65}
              value={ageMax}
              onChange={(e) => onChangeAgeMax(parseInt(e.target.value, 10))}
              className="w-full"
              aria-valuemin={ageLocked ? 18 : 13}
              aria-valuemax={65}
              aria-valuenow={ageMax}
            />
          </div>
        </div>
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-2">
          Genders
        </p>
        <div role="group" aria-label="Genders" className="flex gap-2">
          {(['MALE', 'FEMALE'] as const).map((g) => {
            const checked = genders.includes(g);
            const onlyOneSelected = genders.length === 1 && checked;
            const disabled = gendersLocked || onlyOneSelected;
            return (
              <button
                key={g}
                type="button"
                onClick={() => toggleGender(g)}
                aria-pressed={checked}
                disabled={disabled}
                className={cn(
                  'flex-1 p-3 border-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                  checked
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-700',
                  disabled && 'cursor-not-allowed opacity-80',
                )}
                title={
                  gendersLocked
                    ? 'Gender targeting is locked because the parent campaign has a regulated special ad category.'
                    : onlyOneSelected
                    ? "At least one gender must remain selected — pick the other to switch."
                    : undefined
                }
              >
                {g === 'MALE' ? 'Men' : 'Women'}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};
